"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import {
  notificationsApi,
  type InAppNotification,
} from "@/lib/api/endpoints";
import { useSession } from "@/lib/auth/session";

interface NotificationsContextValue {
  unreadCount: number;
  items: InAppNotification[];
  total: number;
  loading: boolean;
  error: string | null;
  unreadOnly: boolean;
  setUnreadOnly: (value: boolean) => void;
  refresh: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

const syncedNotificationUsers = new Set<string>();

export function resetNotificationsSync(): void {
  syncedNotificationUsers.clear();
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const isExcludedRoute = Boolean(
    pathname && (
      pathname.includes("/manage/dashboard") ||
      pathname.includes("/manage/") ||
      pathname === "/bookmarks" ||
      pathname.startsWith("/bookmarks/")
    )
  );

  const { status, user } = useSession();
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [items, setItems] = useState<InAppNotification[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);

  const fetchingRef = useRef<boolean>(false);

  const fetchNotifications = useCallback(
    async (isUnreadOnly = unreadOnly) => {
      if (isExcludedRoute) return;
      if (status !== "authenticated" || !user) {
        setUnreadCount(0);
        setItems([]);
        setTotal(0);
        return;
      }

      if (fetchingRef.current) return;
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const response = await notificationsApi.list({
          unread_only: isUnreadOnly,
          limit: 50,
          offset: 0,
        });
        setItems(response.items);
        setTotal(response.total);
        setUnreadCount(response.unread_count);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to load notifications";
        setError(message);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [isExcludedRoute, status, user, unreadOnly]
  );

  // Fetch only ONCE per authenticated session. Never fetch on admin routes or bookmarks.
  useEffect(() => {
    if (isExcludedRoute) return;
    if (status === "authenticated" && user) {
      if (!syncedNotificationUsers.has(user.id)) {
        syncedNotificationUsers.add(user.id);
        void fetchNotifications(unreadOnly);
      }
    } else if (status === "anonymous") {
      if (syncedNotificationUsers.size > 0) {
        syncedNotificationUsers.clear();
        setUnreadCount(0);
        setItems([]);
        setTotal(0);
        setError(null);
      }
    }
  }, [isExcludedRoute, status, user?.id, unreadOnly]);

  const markAsRead = useCallback(
    async (id: string) => {
      // Optimistic update
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, is_read: true, read_at: new Date().toISOString() } : item))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      try {
        const res = await notificationsApi.markRead(id);
        if (typeof res.unread_count === "number") {
          setUnreadCount(res.unread_count);
        }
      } catch {
        // Silently preserve optimistic state
      }
    },
    []
  );

  const markAllAsRead = useCallback(async () => {
    // Optimistic update
    setItems((prev) =>
      prev.map((item) => ({ ...item, is_read: true, read_at: item.read_at || new Date().toISOString() }))
    );
    setUnreadCount(0);

    try {
      const res = await notificationsApi.markAllRead();
      if (typeof res.unread_count === "number") {
        setUnreadCount(res.unread_count);
      }
    } catch {
      // Silently preserve optimistic state
    }
  }, []);

  // Explicit refresh only (e.g. user clicks refresh in profile/notifications)
  const refresh = useCallback(async () => {
    await fetchNotifications(unreadOnly);
  }, [fetchNotifications, unreadOnly]);

  const value = useMemo(
    () => ({
      unreadCount,
      items,
      total,
      loading,
      error,
      unreadOnly,
      setUnreadOnly,
      refresh,
      markAsRead,
      markAllAsRead,
    }),
    [
      unreadCount,
      items,
      total,
      loading,
      error,
      unreadOnly,
      setUnreadOnly,
      refresh,
      markAsRead,
      markAllAsRead,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationsProvider");
  }
  return context;
}
